import { Component, EventEmitter, Input, OnDestroy, OnInit, Output, SimpleChanges, OnChanges } from '@angular/core';
import {
  BatchRatesTableButtonEventData,
  BatchRatesTableDropDownOnClickData,
  DeliveryTimeStatus,
  ProccessedSingleOrderRate,
} from '@app/services/order-batch/order-batch-rates.model';
import moment from 'moment';

import {
  DropdownOption,
  DROPDOWN_OPTIONS_FIELD,
  SaveAsBatchColumnSettings,
  SaveAsBatchColumnTypes,
  SaveAsBatchEventNames,
  SmartBatchColumnsProps,
} from '../../configs/saveasbatch-table.config';
import { takeUntil } from 'rxjs/operators';
import { TableService } from '@app/services/table/table.service';
import { Subject } from 'rxjs';
import { ServiceLevelDisplayNames } from '@pdqdev/pdq-shared/dist/CarrierData';

const enum DROPDOWN_DATA_FIELDS {
  CELLS = 'cells',
  HEADERS = 'headers',
}
type ServiceLevelKeys = keyof typeof ServiceLevelDisplayNames;

@Component({
  selector: 'pdq-save-as-batch-table',
  templateUrl: './save-as-batch-table.component.html',
  styleUrls: ['./save-as-batch-table.component.scss'],
  standalone: false,
})
export class SaveAsBatchTableComponent implements OnInit, OnDestroy, OnChanges {
  @Input() rows: ProccessedSingleOrderRate[];
  @Input() columnsConfig: SaveAsBatchColumnSettings[];
  @Input() isDisableHeaderButtons: boolean = false;
  @Input() currentTab: ServiceLevelKeys;
  @Output() changeColumnDropdownOption = new EventEmitter<BatchRatesTableDropDownOnClickData>();
  @Output() fireColumnButtonEvent = new EventEmitter<BatchRatesTableButtonEventData>();
  @Output('onRetryButtonClick') retryEventEmmiter = new EventEmitter<ProccessedSingleOrderRate>();
  @Output() sort = new EventEmitter<number>();
  @Output() onChangeSaturdayDelivery = new EventEmitter<{ orderId: string; isSaturdayDelivery: boolean }>();
  @Output('onSelectRow') onRowSelected = new EventEmitter<string[]>();
  readonly ColumnTypes = SaveAsBatchColumnTypes;
  readonly DeliveryTimePredictionStatus = DeliveryTimeStatus;
  readonly SmartBatchColumnsProps = SmartBatchColumnsProps;
  isSelectedRows = false;
  selected: ProccessedSingleOrderRate[] = [];
  selectedIds: string[] = [];
  selectedRowsNumber = 0;
  openedMenusData = {
    [DROPDOWN_DATA_FIELDS.HEADERS]: {},
    [DROPDOWN_DATA_FIELDS.CELLS]: {},
  };
  lastSelectedRow;
  private destroyed$ = new Subject<any>();
  focusedRowId;
  commonDropDownOptions = {
    [SmartBatchColumnsProps.SERVICE_LEVEL_OPTIONS]: [],
    [SmartBatchColumnsProps.BOXES_OPTIONS]: [],
  };
  constructor(private tableService: TableService) {}
  ngOnDestroy(): void {
    this.destroyed$?.next(true);
    this.destroyed$?.complete();
  }

  ngOnInit(): void {
    this.lastSelectedRow = this.rows[0];
    this.tableService.popoverPressedRowId$.pipe(takeUntil(this.destroyed$)).subscribe((rowId) => {
      this.focusedRowId = rowId;
    });
  }

  ngOnChanges(changes: SimpleChanges) {
    this.selected = this.rows.filter((value) => this.selectedIds.includes(value.orderId));
  }

  onSelectRow(event): void {
    if (event?.selected) {
      this.selected = event.selected;
      this.selectedIds = this.selected.map?.((selectedRow) => selectedRow.orderId);
      this.isSelectedRows = event.selected?.length > 0;
      this.selectedRowsNumber = this.selectedIds?.length ?? 0;
      this.commonDropDownOptions = {
        [SmartBatchColumnsProps.SERVICE_LEVEL_OPTIONS]: this.getCommonColumnDropdownOptions(
          this.columnsConfig.find((x) => x.dropdownOptionsProp === SmartBatchColumnsProps.SERVICE_LEVEL_OPTIONS),
        ),
        [SmartBatchColumnsProps.BOXES_OPTIONS]: this.getCommonColumnDropdownOptions(
          this.columnsConfig.find((x) => x.dropdownOptionsProp === SmartBatchColumnsProps.BOXES_OPTIONS),
        ),
      };
      this.onRowSelected.emit(this.selectedIds);
    }
  }

  checkIfCellIsSelected(row: ProccessedSingleOrderRate): boolean {
    return this.selectedIds.includes(row.orderId);
  }

  getSortingIconName(index: number): string {
    return this.columnsConfig[index].sortDirection
      ? this.columnsConfig[index].sortDirection === 'ASC'
        ? 'icon-sorted-asc.svg'
        : 'icon-sorted-desc.svg'
      : 'icon-not-sorted.svg';
  }

  sortTable(index: number): void {
    this.sort.emit(index);
  }

  // TODO: eliminate usage of order number
  menuOpened(isHeader: boolean, index: number, orderNumber?: number) {
    const prop = this.getDropdownDataField(isHeader);

    if (isHeader) {
      this.openedMenusData[prop][index] = true;
    } else {
      if (!this.openedMenusData[prop][index]) {
        this.openedMenusData[prop][index] = {};
      }
      this.openedMenusData[prop][index][orderNumber] = true;
    }
  }

  // TODO: eliminate usage of order number
  menuClosed(isHeader: boolean, index: number, orderNumber?: number) {
    const prop = this.getDropdownDataField(isHeader);
    if (isHeader) {
      this.openedMenusData[prop][index] = false;
    } else {
      this.openedMenusData[prop][index][orderNumber] = false;
    }
  }

  // TODO: eliminate usage of order number
  checkIfDropdownIsOpened(isHeader: boolean, index: number, orderNumber?: number) {
    const prop = this.getDropdownDataField(isHeader);

    if (isHeader) {
      if (this.openedMenusData[prop][index] === true) {
        return true;
      }
      return false;
    } else {
      if (this.openedMenusData[prop][index]?.[orderNumber] === true) {
        return true;
      }
      return false;
    }
  }

  private getDropdownDataField(isHeader: boolean) {
    const prop = isHeader ? DROPDOWN_DATA_FIELDS.HEADERS : DROPDOWN_DATA_FIELDS.CELLS;
    return prop;
  }

  onDropdownButtonClick(option: DropdownOption, column: SaveAsBatchColumnSettings, row: ProccessedSingleOrderRate) {
    const property = column?.valueProp || column?.prop;

    this.changeColumnDropdownOption.emit({
      selectedOption: option,
      property: property,
      // TODO: pass here row itself, not 'order number' property
      ordersIds: [row.orderId],
    });
  }

  onDropdownButtonClickHeader(option: DropdownOption & { fieldName: string }, column: SaveAsBatchColumnSettings) {
    const property = column?.valueProp || column?.prop;
    let rowToOptionMap;
    switch (column.dropdownOptionsProp) {
      case SmartBatchColumnsProps.SERVICE_LEVEL_OPTIONS:
        rowToOptionMap = new Map<string, DropdownOption>();
        this.rows.map((row) => {
          if (this.selectedIds.includes(row.orderId)) {
            const rowOption = this.getSingleColumnDropdownOptions(row, column)?.find(
              (x) => x[option.fieldName] === option[option.fieldName],
            );
            rowToOptionMap.set(row.orderId, rowOption);
          }
        });
        break;
    }
    this.changeColumnDropdownOption.emit({
      selectedOption: option,
      property: property,
      ordersIds: this.selectedIds,
      rowToOptionMap: rowToOptionMap,
    });
  }

  getCommonColumnDropdownOptions(column: SaveAsBatchColumnSettings) {
    // this generates all the options for the dropdown in the header, based on the selected rows
    const prop = column?.dropdownOptionsProp;
    if (prop) {
      const options: DropdownOption[][] = this.selected.map((val) => {
        return val[prop];
      });
      let field,
        mainTextField = 'mainText';
      switch (prop) {
        case SmartBatchColumnsProps.SERVICE_LEVEL_OPTIONS:
          field = 'token';
          mainTextField = 'name';
          break;
        case SmartBatchColumnsProps.BOXES_OPTIONS:
        default:
          field = 'value';
          break;
      }
      return this.generateCommonOptionsArray(options, field, mainTextField);
    }

    return [];
  }

  getSingleColumnDropdownOptions(row: any, column: SaveAsBatchColumnSettings) {
    return row?.[column?.[DROPDOWN_OPTIONS_FIELD]] ?? [];
  }

  generateCommonOptionsArray(
    options: DropdownOption[][],
    field: keyof SaveAsBatchColumnSettings,
    mainTextField: string,
  ) {
    if (!(options?.length > 0)) {
      return [];
    }
    const commonOptions = new Map();
    for (const rowOptionArray of options) {
      for (const option of rowOptionArray) {
        if (!commonOptions.has(option[field])) {
          const optionToAdd = {
            [field]: option[field],
            mainText: option[mainTextField] ?? option.mainText,
            fieldName: field,
          };
          commonOptions.set(option[field], optionToAdd);
        }
      }
    }
    const commonOptionsArray = Array.from(commonOptions.values());
    return commonOptionsArray;
  }

  getRowTextValue(row: ProccessedSingleOrderRate, property: string) {
    const data = row[property];
    if (data && (!isNaN(data) || data.length > 0)) {
      return data;
    } else {
      return 'Missing data';
    }
  }

  deliveryTimeDisplay(row: ProccessedSingleOrderRate): any {
    const promiseDate = row.orderPromiseDeliveryDate;

    if (row.orderIsPickup) {
      return 'For pickup';
    } else if (!promiseDate || !(promiseDate instanceof Date)) {
      return 'Unknown';
    } else {
      return moment(promiseDate).format('MMM DD, YYYY');
    }
  }

  getDeliveryPromiseText(row: ProccessedSingleOrderRate): string {
    return row.deliveryTimePredictionStatusPrint;
  }

  getHeaderDropdownClass(index: number, column: SaveAsBatchColumnSettings) {
    if (!(this.commonDropDownOptions[column.dropdownOptionsProp]?.length > 0)) {
      return 'table-component__cell-header-dropdown-button-disabled';
    } else if (this.checkIfDropdownIsOpened(true, index)) {
      return 'table-component__cell-header-dropdown-button-opened';
    } else if (!this.checkIfDropdownIsOpened(true, index)) {
      return 'table-component__cell-header-dropdown-button-closed';
    }
  }

  getCellDropdownClass(index: number, column: SaveAsBatchColumnSettings, row: any) {
    if (!(this.getSingleColumnDropdownOptions(row, column)?.length > 0)) {
      return 'table-component__cell-dropdown-button-disabled';
    } else if (this.checkIfDropdownIsOpened(false, index, row.orderNumber)) {
      return 'table-component__cell-dropdown-button-opened';
    } else if (!this.checkIfDropdownIsOpened(false, index, row.orderNumber)) {
      return 'table-component__cell-dropdown-button-closed';
    }
  }

  onClickColumnButton(row: ProccessedSingleOrderRate, buttonEvent: SaveAsBatchEventNames) {
    this.fireColumnButtonEvent.emit({
      event: buttonEvent,
      ordersIds: [row.orderId],
    });
  }

  onClickColumnButtonHeader(selectedOrdersIds: string[], buttonEvent: SaveAsBatchEventNames) {
    this.fireColumnButtonEvent.emit({
      event: buttonEvent,
      ordersIds: selectedOrdersIds,
    });
  }

  isShowMainColumnErrorIcon(row: ProccessedSingleOrderRate) {
    return !!row?.isShowMissingRatesIcon;
  }

  isShowMainColumnInfoIcon(row: ProccessedSingleOrderRate) {
    return !row?.isShowMissingRatesIcon && (row?.isShouldTrySuggestedAddress || row?.isTriedSuggestedAddress);
  }

  isShowMainColumnWarningIcon(row: ProccessedSingleOrderRate) {
    return !row?.isShowMissingRatesIcon && row?.warningMessages?.length > 0;
  }

  onRetryButtonClick(orderData: ProccessedSingleOrderRate) {
    this.retryEventEmmiter.emit(orderData);
  }

  onChangeSaturdayDeliveryClick(isSaturdayDelivery: boolean, row: ProccessedSingleOrderRate) {
    this.onChangeSaturdayDelivery.emit({ orderId: row?.orderId, isSaturdayDelivery: isSaturdayDelivery });
  }

  onActivate(event): void {
    const range = document.getSelection()?.toString()?.length ? document.getSelection().getRangeAt(0) : null;
    document.getSelection().removeAllRanges();
    if (range && !event.event.shiftKey) {
      document.getSelection().addRange(range);
    }

    if (event.type !== 'click') {
      return;
    }
    if (event.event.shiftKey && !event.cellIndex && this.lastSelectedRow) {
      const index = this.rows.findIndex((r) => r.orderId === this.lastSelectedRow.orderId);
      const selectedIndex = this.rows.findIndex((r) => r.orderId === event.row.orderId);
      const startIndex = selectedIndex > index ? index : selectedIndex;
      const endIndex = selectedIndex > index ? selectedIndex : index;
      const isSelected = this.selected.findIndex((row) => row.orderId === event.row.orderId) > -1;
      for (let i = startIndex; i <= endIndex; i++) {
        const findIndex = this.selected.findIndex((x) => x.orderId == this.rows[i].orderId);
        if (!isSelected) {
          if (findIndex === -1) {
            this.selected.push(this.rows[i]);
          }
        } else if (findIndex !== -1) {
          this.selected.splice(findIndex, 1);
        }
      }
      this.onSelectRow({ selected: this.selected });
    }
    this.lastSelectedRow = event.row;
  }

  selectAll() {
    if (this.selected.length) {
      this.selected = [];
    } else {
      this.selected = this.rows.slice();
    }
    this.onSelectRow({ selected: this.selected });
  }

  isServiceLevelNotPreferred(recommendedServiceLevelNames, serviceLevelName, serviceLevelOptions) {
    const serviceLevelToken = serviceLevelOptions?.find((x) => x.name === serviceLevelName)?.token;
    return (
      recommendedServiceLevelNames?.length &&
      serviceLevelToken &&
      !recommendedServiceLevelNames.some((x) => x === serviceLevelToken)
    );
  }

  toDisplayAssignedIcon(column, row) {
    return (
      column['prop'] === SmartBatchColumnsProps.SERVICE_LEVEL_NAME &&
      this.currentTab == 'PDQ_CHOISE' &&
      row.serviceLevelName &&
      (row.switchedToSelectedRate === row.serviceLevelName ||
        (!row.switchedToSelectedRate &&
          (row.nonRecommendedServiceLevel === row.serviceLevelName ||
            this.isServiceLevelNotPreferred(
              row.recommendedServiceLevelNames,
              row.serviceLevelName,
              row.serviceLevelOptions,
            ))))
    );
  }
}
